import React, { useEffect, useRef, useState } from 'react';
import { SuggestComboBox, SuggestComboBoxProps } from '../AbstractComboBox/SuggestComboBox';
import { TopContentResult } from '../AbstractComboBox/types/SuggestResultTypes';
import { useHtmlValidId } from '../../../../../../hooks/useHtmlValidId';
import { useSonTranslation } from '../../../../../../translation/useSonTranslation';
import getContentTopResults from './getContentTopResult';
import ListItemPage from '../AbstractComboBox/ListItemPage/ListItemPage';
import { Namespace } from '../../../../../../translation/namespace';
import { sendLiveRegionMessage } from '../../../../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { SelectList } from '../../../../../../_lib/Form2/elements/SelectList/SelectList';
import { SelectListItemHiddenInput } from '../../../../../../_lib/Form2/elements/SelectList/SelectListItemHiddenInput';
import { SelectListItemBasic } from '../../../../../../_lib/Form2/elements/SelectList/SelectListItemBasic';

interface GroupPreviewMultiSelectComboBoxForForm2Props
  extends Omit<SuggestComboBoxProps<TopContentResult>, 'id' | 'onSelection' | 'validationMessage'> {
  valueKey?: string;
  hasReadProtectedItems: boolean;
  value: TopContentResult[] | undefined;
}

/**
 * Form2 compatible ContentMultiSelectComboBox that allows to render "GroupPreview"s.
 * As with most combo boxes the name of the input should not be set.
 * Here we use the name as the hidden input for the selected page.
 * The ComboBox is responsible to set the hidden parameter, HTML conform JSON payload.
 */
export function GroupPreviewMultiSelectComboBoxForForm2({
  value,
  required,
  name,
  hasReadProtectedItems,
  ...otherProps
}: Readonly<GroupPreviewMultiSelectComboBoxForForm2Props>) {
  const id = useHtmlValidId();
  const { t } = useSonTranslation('FormsComboBox');
  const ariaControlsId = useHtmlValidId();
  const [results, setResults] = useState<TopContentResult[] | undefined>(value ?? []);
  const [deleteMessage, setDeleteMessage] = useState('');
  const suggestComboBoxRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (deleteMessage) {
      sendLiveRegionMessage(deleteMessage);
    }
  }, [deleteMessage]);

  return (
    <div className="forms-combobox-multiselect__container">
      <SuggestComboBox
        {...otherProps}
        ref={suggestComboBoxRef}
        name={undefined} // need to overwrite name here after we spread props !
        id={`content-select-combobox-${id}`}
        responseMapper={getContentTopResults}
        onSelection={(item) => {
          if (item && results?.every(i => i.id !== item.id)) {
            setResults([item as TopContentResult, ...(results as TopContentResult[])]);
          }
        }}
        validationMessage={required && results?.length === 0 ? t('content.validationMessage') : undefined}
        required={required}
      >
        {item => <ListItemPage page={item} />}
      </SuggestComboBox>
      {results && results.length > 0 && (
        <SelectList
          id={ariaControlsId}
          headline={t(`${Namespace.FormsComboBoxMultiSelect}:resultlist.selectionHeadline`)}
          shouldHaveSkipLinks={results.length >= 5}
          headlineIdPrefix="group-preview-multiselect-combobox-for-form2"
        >
          {results.map((result, index) => (
            <SelectListItemHiddenInput
              key={result.id}
              title={result.title}
              name={`${name}[${index}].id`}
              value={result.id}
              deleteButtonTitle={t('FormsComboBoxMultiSelect:resultlist.deleteButtonText', { itemName: result.title })}
              deleteElement={() => {
                setResults(results?.filter(element => element.id !== result.id));
                setDeleteMessage(t(`${Namespace.FormsComboBoxMultiSelect}:deleted`, { item: result.title }));
                suggestComboBoxRef.current?.focus();
              }}
              ariaControls={ariaControlsId}
            />
          ))}
          {hasReadProtectedItems && (
            <SelectListItemBasic title={t(`${Namespace.FormsComboBoxMultiSelect}:resultlist.hasHiddenItems`)} />
          )}
        </SelectList>
      )}
    </div>
  );
}
